import type { EmbeddedProduct } from 'modules/supplier/products';
import type { ActivityType } from 'types';
import { asMediaTargetPath } from 'modules/agreement/discountTree';
import {
  productToChainPath,
  targetIdFromPath,
  type ChainPath,
} from 'modules/supplier/supplier/components/ProductChainPicker';
import type { ClientTargetPath } from './types';

/** Narrow a catalogue path to a client discount target. */
export function asClientTargetPath(path: ChainPath): ClientTargetPath {
  const [root, parent] = path;
  if (root?.kind === 'vendor') {
    return asMediaTargetPath(path);
  }
  if (root?.kind === 'service-category') {
    if (path.length === 1) return [root];
    if (path.length === 2 && parent?.kind === 'product' && parent.productKind === 'SERVICE') {
      return [root, { ...parent, productKind: 'SERVICE' }];
    }
  }
  throw new Error('A client target must be a media vendor path or service-category path.');
}

export function clientTargetPathFromProduct(product: EmbeddedProduct): ClientTargetPath {
  return asClientTargetPath(productToChainPath(product));
}

export function clientTargetKey(path: ClientTargetPath): string {
  return targetIdFromPath(path);
}

export function clientTargetInvalidReason(path: ClientTargetPath, type: ActivityType): string | null {
  const root = path[0];
  if (root.kind === 'vendor') {
    const outlet = path.length > 1 ? path[1] : undefined;
    if (outlet?.kind === 'outlet' && outlet.type != null && outlet.type !== type) return 'TARGET_TYPE_MISMATCH';
  }
  if (root.kind === 'service-category' && root.type != null && root.type !== type) {
    return 'TARGET_TYPE_MISMATCH';
  }
  const endpoint = path[path.length - 1];
  if (endpoint.kind === 'product') {
    if (endpoint.type != null && endpoint.type !== type) return 'TARGET_TYPE_MISMATCH';
    const expectedKind = root.kind === 'service-category' ? 'SERVICE' : 'MEDIA';
    if (endpoint.productKind !== expectedKind) return 'TARGET_KIND_MISMATCH';
  }
  return null;
}

export function expandClientTargetPrefixes(
  paths: readonly ChainPath[],
  { replaceDuplicates = true }: { replaceDuplicates?: boolean } = {},
): ClientTargetPath[] {
  const targets = new Map<string, ClientTargetPath>();
  for (const candidate of paths) {
    const path = asClientTargetPath(candidate);
    path.forEach((_, index) => {
      const prefix = asClientTargetPath(path.slice(0, index + 1) as ChainPath);
      const key = clientTargetKey(prefix);
      if (replaceDuplicates || !targets.has(key)) targets.set(key, prefix);
    });
  }
  return [...targets.values()];
}
